import React from 'react';
import PropTypes from 'prop-types';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;

function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('No window'));
  }
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile));
      existing.addEventListener('error', () => reject(new Error('Turnstile failed to load')));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error('Turnstile failed to load'));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

class TurnstileWidget extends React.Component {
  widgetId = null;
  // Bumped on every render/remove so a script load that resolves late can't render a stale widget.
  generation = 0;
  containerRef = React.createRef();

  componentDidMount() {
    this.renderWidget();
  }

  componentWillUnmount() {
    this.removeWidget();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.siteKey !== this.props.siteKey ||
      prevProps.action !== this.props.action ||
      prevProps.theme !== this.props.theme ||
      prevProps.size !== this.props.size ||
      prevProps.resetSignal !== this.props.resetSignal
    ) {
      this.removeWidget();
      this.renderWidget();
    }
  }

  removeWidget = () => {
    this.generation += 1;
    if (
      this.widgetId !== null &&
      typeof window !== 'undefined' &&
      window.turnstile &&
      typeof window.turnstile.remove === 'function'
    ) {
      try {
        window.turnstile.remove(this.widgetId);
      } catch (e) {
        // ignore
      }
    }
    this.widgetId = null;
  };

  renderWidget = () => {
    const { siteKey, action, theme, size } = this.props;
    if (!siteKey || !this.containerRef.current) {
      return;
    }

    this.generation += 1;
    const generation = this.generation;
    const isCurrent = () => generation === this.generation;

    loadTurnstileScript()
      .then((turnstile) => {
        if (!isCurrent() || !this.containerRef.current || !turnstile) {
          return;
        }
        this.containerRef.current.innerHTML = '';
        this.widgetId = turnstile.render(this.containerRef.current, {
          sitekey: siteKey,
          theme: theme || 'dark',
          size: size || 'normal',
          action: action || undefined,
          callback: (token) => {
            if (isCurrent() && this.props.onVerify) this.props.onVerify(token);
          },
          'expired-callback': () => {
            if (isCurrent() && this.props.onExpire) this.props.onExpire();
          },
          'error-callback': () => {
            if (isCurrent() && this.props.onError) this.props.onError();
          }
        });
      })
      .catch(() => {
        if (isCurrent() && this.props.onError) this.props.onError();
      });
  };

  render() {
    if (!this.props.siteKey) {
      return null;
    }

    return (
      <div className="contact-turnstile">
        <div ref={this.containerRef} />
      </div>
    );
  }
}

TurnstileWidget.propTypes = {
  siteKey: PropTypes.string,
  action: PropTypes.string,
  theme: PropTypes.string,
  size: PropTypes.oneOf(['normal', 'compact', 'flexible']),
  resetSignal: PropTypes.number,
  onVerify: PropTypes.func,
  onExpire: PropTypes.func,
  onError: PropTypes.func
};

TurnstileWidget.defaultProps = {
  siteKey: '',
  action: '',
  theme: 'dark',
  size: 'normal',
  resetSignal: 0,
  onVerify: undefined,
  onExpire: undefined,
  onError: undefined
};

export default TurnstileWidget;
